import React, { useEffect, useState } from "react";
import api, { formatDate, formatDateTime, formatErrorDetail } from "@/lib/api";
import { PageHeader, Surface, TableWrap, Empty } from "@/components/app/Primitives";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Plus, Trash2, ShieldCheck, Clock, CheckCircle2, XCircle } from "lucide-react";

const KIND_LABEL = { sss: "SSS", philhealth: "PhilHealth", pagibig: "Pag-IBIG", bir_withholding: "BIR Withholding" };
const SCALAR_FIELDS = {
    philhealth: [["rate", "Premium rate"], ["floor", "Floor"], ["ceiling", "Ceiling"]],
    pagibig: [["salary_threshold", "Threshold"], ["ee_low", "EE low"], ["ee_high", "EE high"], ["er_rate", "ER rate"], ["max_base", "Max base"], ["max_contribution", "Max contribution"]],
};
const STATUS_BADGE = { pending: "badge badge-pending", approved: "badge badge-approved", rejected: "badge badge-draft", failed: "badge badge-destructive" };

export default function PayrollSettings() {
    const { user } = useAuth();
    const roleSet = new Set((user?.roles || []).map((r) => r.role));
    const isHrManager = roleSet.has("hr_manager");
    const isApprover = roleSet.has("accounting_manager");

    const [data, setData] = useState(null);
    const [requests, setRequests] = useState([]);

    const load = async () => {
        try {
            const [o, r] = await Promise.all([api.get("/payroll-settings/overview"), api.get("/payroll-settings/requests")]);
            setData(o.data); setRequests(r.data.items || []);
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed to load"); }
    };
    useEffect(() => { load(); }, []);
    if (!data) return null;

    const pending = requests.filter((r) => r.status === "pending");

    return (
        <div>
            <PageHeader
                title="Payroll Settings"
                subtitle="Wage orders, regional minimum wage, and government contribution tables. Changes are proposed by an HR Manager and take effect only after Accounting Manager approval."
            />
            <div className="flex items-center gap-2 mb-3 text-xs">
                <ShieldCheck className="size-4 text-primary" />
                <span className="text-muted-foreground">Your access:</span>
                {isHrManager && <span className="badge badge-info">HR Manager · can propose</span>}
                {isApprover && <span className="badge badge-approved">Accounting Manager · can approve</span>}
                {!isHrManager && !isApprover && <span className="badge badge-draft">View only</span>}
                {pending.length > 0 && <span className="badge badge-pending ml-auto">{pending.length} pending approval{pending.length > 1 ? "s" : ""}</span>}
            </div>

            <Tabs defaultValue="wages">
                <TabsList>
                    <TabsTrigger value="wages" data-testid="tab-wages">Minimum Wage &amp; Wage Orders</TabsTrigger>
                    <TabsTrigger value="contrib" data-testid="tab-contrib">Contribution Tables</TabsTrigger>
                    <TabsTrigger value="tardiness" data-testid="tab-tardiness">Tardiness &amp; Undertime</TabsTrigger>
                    <TabsTrigger value="holiday" data-testid="tab-holiday">Holiday Pay</TabsTrigger>
                    <TabsTrigger value="requests" data-testid="tab-requests">Change Requests{pending.length ? ` (${pending.length})` : ""}</TabsTrigger>
                </TabsList>

                <TabsContent value="wages">
                    <WagesTab data={data} isHrManager={isHrManager} onProposed={load} />
                </TabsContent>
                <TabsContent value="contrib">
                    <ContribTab data={data} isHrManager={isHrManager} onProposed={load} />
                </TabsContent>
                <TabsContent value="holiday">
                    <HolidayPayTab canEdit={roleSet.has("hr_admin") || roleSet.has("super_admin")} />
                </TabsContent>
                <TabsContent value="tardiness">
                    <TardinessTab isHrManager={isHrManager} refreshKey={data} onProposed={load} />
                </TabsContent>
                <TabsContent value="requests">
                    <RequestsTab requests={requests} isApprover={isApprover} currentUserId={user?.id} onChanged={load} />
                </TabsContent>
            </Tabs>
        </div>
    );
}

// ---- shared propose helper ----
async function propose(body, onProposed) {
    try {
        await api.post("/payroll-settings/propose", body);
        toast.success("Change submitted for Accounting Manager approval");
        onProposed();
        return true;
    } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed"); return false; }
}

function ProposeGate({ isHrManager, children }) {
    if (!isHrManager) return <span className="text-xs text-muted-foreground">Only an HR Manager can propose changes.</span>;
    return children;
}

// ---------- Wages tab ----------
function WagesTab({ data, isHrManager, onProposed }) {
    const orders = data.wage_orders?.items || [];
    const floors = data.minimum_wages?.items || [];
    const [woOpen, setWoOpen] = useState(false);
    const [mwOpen, setMwOpen] = useState(false);
    const emptyWo = { region: "NCR", order_no: "", base_daily_minimum: "", notes: "", tranches: [{ increase: "", effective_date: "" }] };
    const [wo, setWo] = useState(emptyWo);
    const [mw, setMw] = useState({ region: "", daily_minimum: "", effective_date: "", note: "" });

    const submitWo = async () => {
        const tranches = wo.tranches.filter((t) => t.increase && t.effective_date).map((t) => ({ increase: Number(t.increase), effective_date: t.effective_date }));
        if (!wo.region || !wo.base_daily_minimum || !tranches.length) return toast.error("Region, base, and at least one tranche required");
        if (await propose({ module: "wage_order", action: "create", payload: { ...wo, base_daily_minimum: Number(wo.base_daily_minimum), tranches } }, onProposed)) { setWoOpen(false); setWo(emptyWo); }
    };
    const submitMw = async () => {
        if (!mw.region || !mw.daily_minimum) return toast.error("Region and daily minimum required");
        if (await propose({ module: "minimum_wage", action: "upsert", payload: { ...mw, daily_minimum: Number(mw.daily_minimum) } }, onProposed)) { setMwOpen(false); setMw({ region: "", daily_minimum: "", effective_date: "", note: "" }); }
    };

    return (
        <div className="space-y-5">
            <div>
                <div className="flex items-center justify-between mb-2">
                    <h3 className="font-display font-semibold">Wage Orders</h3>
                    <ProposeGate isHrManager={isHrManager}>
                        <Dialog open={woOpen} onOpenChange={setWoOpen}>
                            <DialogTrigger asChild><button className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-2" data-testid="propose-wo"><Plus className="size-4" /> Propose Wage Order</button></DialogTrigger>
                            <DialogContent>
                                <DialogHeader><DialogTitle>Propose Wage Order</DialogTitle></DialogHeader>
                                <div className="space-y-2">
                                    <div className="grid grid-cols-2 gap-2">
                                        <div><Label>Region</Label><Input value={wo.region} onChange={(e) => setWo({ ...wo, region: e.target.value })} /></div>
                                        <div><Label>Base Daily Minimum (₱)</Label><Input type="number" value={wo.base_daily_minimum} onChange={(e) => setWo({ ...wo, base_daily_minimum: e.target.value })} /></div>
                                    </div>
                                    <div><Label>Order No.</Label><Input value={wo.order_no} onChange={(e) => setWo({ ...wo, order_no: e.target.value })} placeholder="Wage Order No. NCR-28" /></div>
                                    <div className="flex items-center justify-between"><Label>Tranches</Label><button className="text-xs text-primary flex items-center gap-1" onClick={() => setWo({ ...wo, tranches: [...wo.tranches, { increase: "", effective_date: "" }] })}><Plus className="size-3" /> Add</button></div>
                                    {wo.tranches.map((t, i) => (
                                        <div key={i} className="flex items-end gap-2">
                                            <div className="flex-1"><Label className="text-xs">Increase (₱)</Label><Input type="number" value={t.increase} onChange={(e) => setWo({ ...wo, tranches: wo.tranches.map((x, j) => j === i ? { ...x, increase: e.target.value } : x) })} /></div>
                                            <div className="flex-1"><Label className="text-xs">Effective</Label><Input type="date" value={t.effective_date} onChange={(e) => setWo({ ...wo, tranches: wo.tranches.map((x, j) => j === i ? { ...x, effective_date: e.target.value } : x) })} /></div>
                                            {wo.tranches.length > 1 && <button className="h-9 px-2 text-destructive" onClick={() => setWo({ ...wo, tranches: wo.tranches.filter((_, j) => j !== i) })}><Trash2 className="size-4" /></button>}
                                        </div>
                                    ))}
                                    <div><Label>Notes</Label><Input value={wo.notes} onChange={(e) => setWo({ ...wo, notes: e.target.value })} /></div>
                                </div>
                                <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={submitWo} data-testid="submit-wo">Submit for Approval</button></DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </ProposeGate>
                </div>
                {!orders.length && <Surface className="p-4"><Empty title="No wage orders" /></Surface>}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {orders.map((o) => (
                        <Surface key={o.id} className="p-4">
                            <div className="flex items-start justify-between">
                                <div><div className="font-display font-semibold">{o.title || o.order_no}</div><div className="text-xs text-muted-foreground font-mono">{o.region} · ₱{Number(o.base_daily_minimum).toFixed(2)} → ₱{Number(o.full_floor).toFixed(2)}</div></div>
                                <span className="badge badge-draft">{(o.status || "").replace(/_/g, " ")}</span>
                            </div>
                            <table className="w-full dense-table mt-2">
                                <thead><tr><th>#</th><th className="num">Increase</th><th>Effective</th><th className="num">Floor</th></tr></thead>
                                <tbody>{o.tranches.map((t) => (<tr key={t.seq}><td>{t.seq}</td><td className="num">+₱{Number(t.increase).toFixed(2)}</td><td className="font-mono text-xs">{formatDate(t.effective_date)}</td><td className="num font-semibold">₱{Number(t.resulting_floor).toFixed(2)}</td></tr>))}</tbody>
                            </table>
                        </Surface>
                    ))}
                </div>
            </div>

            <div>
                <div className="flex items-center justify-between mb-2">
                    <h3 className="font-display font-semibold">Regional Minimum Wage Floors</h3>
                    <ProposeGate isHrManager={isHrManager}>
                        <Dialog open={mwOpen} onOpenChange={setMwOpen}>
                            <DialogTrigger asChild><button className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-2" data-testid="propose-mw"><Plus className="size-4" /> Propose Floor</button></DialogTrigger>
                            <DialogContent>
                                <DialogHeader><DialogTitle>Propose Regional Minimum Wage</DialogTitle></DialogHeader>
                                <div className="space-y-2">
                                    <div><Label>Region</Label><Input value={mw.region} onChange={(e) => setMw({ ...mw, region: e.target.value })} placeholder="REGION_VII" /></div>
                                    <div><Label>Daily Minimum (₱)</Label><Input type="number" value={mw.daily_minimum} onChange={(e) => setMw({ ...mw, daily_minimum: e.target.value })} /></div>
                                    <div><Label>Effective Date</Label><Input type="date" value={mw.effective_date} onChange={(e) => setMw({ ...mw, effective_date: e.target.value })} /></div>
                                    <div><Label>Note</Label><Input value={mw.note} onChange={(e) => setMw({ ...mw, note: e.target.value })} /></div>
                                </div>
                                <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={submitMw} data-testid="submit-mw">Submit for Approval</button></DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </ProposeGate>
                </div>
                <TableWrap>
                    <thead><tr><th>Region</th><th className="num">Daily Minimum</th><th>Effective</th><th>Source</th><th>Note</th></tr></thead>
                    <tbody>
                        {floors.map((r) => (
                            <tr key={r.region}><td className="font-mono">{r.region}</td><td className="num font-semibold">₱{Number(r.daily_minimum).toFixed(2)}</td><td className="font-mono text-xs">{r.effective_date || "—"}</td><td>{r.source === "custom" ? <span className="badge badge-approved">custom</span> : <span className="badge badge-draft">default</span>}</td><td className="text-xs">{r.note || "—"}</td></tr>
                        ))}
                    </tbody>
                </TableWrap>
            </div>
        </div>
    );
}

// ---------- Contributions tab ----------
function ContribTab({ data, isHrManager, onProposed }) {
    const eff = data.contributions?.effective || {};
    const kinds = data.contributions?.kinds || ["sss", "philhealth", "pagibig", "bir_withholding"];
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ kind: "philhealth", effective_date: "", note: "", data: {}, json: "", bir: null });

    const openFor = (kind) => {
        const base = eff?.[kind]?.data || {};
        setForm({ kind, effective_date: "", note: "", data: SCALAR_FIELDS[kind] ? { ...base } : {}, json: SCALAR_FIELDS[kind] || kind === "bir_withholding" ? "" : JSON.stringify(base, null, 2), bir: kind === "bir_withholding" ? birToGrid(base) : null });
    };
    const submit = async () => {
        if (!form.effective_date) return toast.error("Effective date required");
        let d;
        if (SCALAR_FIELDS[form.kind]) { d = {}; for (const [k] of SCALAR_FIELDS[form.kind]) d[k] = Number(form.data[k]); }
        else if (form.kind === "bir_withholding") { d = gridToBir(form.bir); if (d.error) return toast.error(d.error); }
        else { try { d = JSON.parse(form.json); } catch { return toast.error("Invalid JSON"); } }
        if (await propose({ module: "contribution_table", action: "create", payload: { kind: form.kind, effective_date: form.effective_date, note: form.note, data: d } }, onProposed)) setOpen(false);
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-2">
                <h3 className="font-display font-semibold">Government Contribution Tables</h3>
                <ProposeGate isHrManager={isHrManager}>
                    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) openFor(form.kind); }}>
                        <DialogTrigger asChild><button className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-2" data-testid="propose-contrib"><Plus className="size-4" /> Propose New Version</button></DialogTrigger>
                        <DialogContent>
                            <DialogHeader><DialogTitle>Propose Contribution Table Version</DialogTitle></DialogHeader>
                            <div className="space-y-2">
                                <div className="grid grid-cols-2 gap-2">
                                    <div><Label>Table</Label><select className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm" value={form.kind} onChange={(e) => openFor(e.target.value)}>{kinds.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}</select></div>
                                    <div><Label>Effective Date</Label><Input type="date" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} /></div>
                                </div>
                                {SCALAR_FIELDS[form.kind] ? (
                                    <div className="grid grid-cols-2 gap-2">{SCALAR_FIELDS[form.kind].map(([k, label]) => (<div key={k}><Label className="text-xs">{label}</Label><Input type="number" step="any" value={form.data[k] ?? ""} onChange={(e) => setForm({ ...form, data: { ...form.data, [k]: e.target.value } })} /></div>))}</div>
                                ) : form.kind === "bir_withholding" && form.bir ? (
                                    <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1" data-testid="bir-grid">
                                        <BirGrid title="Semi-monthly pay" rows={form.bir.semi_monthly} onChange={(rows) => setForm({ ...form, bir: { ...form.bir, semi_monthly: rows } })} />
                                        <BirGrid title="Monthly pay" rows={form.bir.monthly} onChange={(rows) => setForm({ ...form, bir: { ...form.bir, monthly: rows } })} />
                                        <div className="text-[11px] text-muted-foreground">Each row: taxable pay from, tax on the amount below that row (base tax), the rate on the excess, and the amount the excess is measured over. Rows must start at 0 and go up. The rate is a percent.</div>
                                    </div>
                                ) : (
                                    <div><Label className="text-xs">Data (JSON)</Label><textarea className="w-full h-40 rounded-md border border-border bg-background p-2 font-mono text-xs" value={form.json} onChange={(e) => setForm({ ...form, json: e.target.value })} /></div>
                                )}
                                <div><Label>Note</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="e.g. SSS Circular 2027-01" /></div>
                            </div>
                            <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={submit} data-testid="submit-contrib">Submit for Approval</button></DialogFooter>
                        </DialogContent>
                    </Dialog>
                </ProposeGate>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {kinds.map((k) => {
                    const e = eff[k];
                    return (
                        <Surface key={k} className="p-4">
                            <div className="flex items-center justify-between"><div className="font-display font-semibold">{KIND_LABEL[k]}</div><span className="text-xs text-muted-foreground">Effective {formatDate(e?.effective_date)} <span className={e?.source === "custom" ? "badge badge-approved" : "badge badge-draft"}>{e?.source}</span></span></div>
                            <div className="text-xs text-muted-foreground mt-2 font-mono break-words">{summarizeKind(k, e?.data)}</div>
                            {k === "bir_withholding" && e?.data && (
                                <details className="mt-2" data-testid="bir-current">
                                    <summary className="text-xs cursor-pointer text-primary">Show the table in force</summary>
                                    {[["Semi-monthly", e.data.semi_monthly], ["Monthly", e.data.monthly]].map(([t, rows]) => (
                                        <div key={t} className="mt-2">
                                            <div className="text-[11px] font-semibold">{t}</div>
                                            <table className="w-full text-[11px] font-mono">
                                                <thead><tr className="text-left text-muted-foreground"><th>From</th><th>Base tax</th><th>Rate</th><th>Over</th></tr></thead>
                                                <tbody>{(rows || []).map((r, i) => (<tr key={i}><td>{r[0]}</td><td>{r[1]}</td><td>{Number((r[2] * 100).toFixed(4))}%</td><td>{r[3]}</td></tr>))}</tbody>
                                            </table>
                                        </div>
                                    ))}
                                </details>
                            )}
                        </Surface>
                    );
                })}
            </div>
        </div>
    );
}

const toGrid = (rows) => (rows || []).map((r) => [String(r[0]), String(r[1]), String(Number((r[2] * 100).toFixed(4))), String(r[3])]);
function birToGrid(d) { return { semi_monthly: toGrid(d.semi_monthly), monthly: toGrid(d.monthly) }; }
function gridToBir(g) {
    const out = {};
    for (const [key, label] of [["semi_monthly", "Semi-monthly"], ["monthly", "Monthly"]]) {
        const rows = [];
        for (const [i, r] of (g?.[key] || []).entries()) {
            const n = r.map((x) => (String(x).trim() === "" ? NaN : Number(x)));
            if (n.some((x) => Number.isNaN(x))) return { error: `${label} row ${i + 1}: every box needs a number` };
            rows.push([n[0], n[1], Number((n[2] / 100).toFixed(6)), n[3]]);
        }
        out[key] = rows;
    }
    return out;
}
function BirGrid({ title, rows, onChange }) {
    const set = (i, j, v) => onChange(rows.map((r, a) => (a === i ? r.map((x, b) => (b === j ? v : x)) : r)));
    return (
        <div>
            <div className="text-xs font-semibold mb-1">{title}</div>
            <div className="grid grid-cols-[1fr_1fr_0.7fr_1fr_auto] gap-1 text-[10px] text-muted-foreground"><span>From</span><span>Base tax</span><span>Rate %</span><span>Over</span><span /></div>
            {rows.map((r, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_0.7fr_1fr_auto] gap-1 mt-1">
                    {r.map((v, j) => (<Input key={j} className="h-8 text-xs" type="number" step="any" value={v} onChange={(e) => set(i, j, e.target.value)} />))}
                    <button type="button" className="px-1 text-destructive" onClick={() => onChange(rows.filter((_, a) => a !== i))} title="Remove this row"><Trash2 className="size-4" /></button>
                </div>
            ))}
            <button type="button" className="mt-1 text-xs text-primary" onClick={() => onChange([...rows, ["", "", "", ""]])}>+ Add row</button>
        </div>
    );
}

function summarizeKind(kind, d) {
    if (!d) return "—";
    if (kind === "philhealth") return `rate ${(d.rate * 100).toFixed(2)}% · floor ₱${d.floor} · ceiling ₱${d.ceiling}`;
    if (kind === "pagibig") return `EE ${(d.ee_high * 100).toFixed(1)}% · ER ${(d.er_rate * 100).toFixed(1)}% · max ₱${d.max_contribution}`;
    if (kind === "sss") return `${(d.brackets || []).length} MSC brackets`;
    if (kind === "bir_withholding") return `${(d.semi_monthly || []).length} semi-monthly + ${(d.monthly || []).length} monthly brackets`;
    return "—";
}

// ---------- Requests tab ----------
function RequestsTab({ requests, isApprover, currentUserId, onChanged }) {
    const act = async (id, action, note) => {
        try {
            await api.post(`/payroll-settings/requests/${id}/${action}`, action === "reject" ? { note: note || "" } : {});
            toast.success(action === "approve" ? "Approved and applied" : "Rejected");
            onChanged();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed"); }
    };
    if (!requests.length) return <Surface className="p-4"><Empty title="No change requests" subtitle="Proposed changes will appear here for approval." /></Surface>;
    return (
        <TableWrap>
            <thead><tr><th>Change</th><th>Module</th><th>Requested by</th><th>When</th><th>Status</th><th></th></tr></thead>
            <tbody>
                {requests.map((r) => {
                    const own = r.requested_by === currentUserId;
                    return (
                        <tr key={r.id} data-testid={`req-${r.id}`}>
                            <td className="text-sm">{r.summary}</td>
                            <td className="text-xs font-mono">{r.module}.{r.action}</td>
                            <td className="text-xs">{r.requested_by_email || "—"}</td>
                            <td className="text-xs text-muted-foreground">{formatDateTime(r.requested_at)}</td>
                            <td><span className={STATUS_BADGE[r.status] || "badge badge-draft"}>{r.status}</span>{r.status === "failed" && r.result?.error && <div className="text-[10px] text-destructive mt-1">{r.result.error}</div>}{r.status === "rejected" && r.decision_note && <div className="text-[10px] text-muted-foreground mt-1">“{r.decision_note}”</div>}</td>
                            <td>
                                {r.status === "pending" && isApprover && !own && (
                                    <div className="flex items-center gap-2">
                                        <button className="text-xs text-emerald-600 flex items-center gap-1" onClick={() => act(r.id, "approve")} data-testid={`approve-${r.id}`}><CheckCircle2 className="size-3" /> Approve</button>
                                        <button className="text-xs text-destructive flex items-center gap-1" onClick={() => { const n = window.prompt("Reason for rejection (optional):", ""); act(r.id, "reject", n); }} data-testid={`reject-${r.id}`}><XCircle className="size-3" /> Reject</button>
                                    </div>
                                )}
                                {r.status === "pending" && (own || !isApprover) && <span className="text-[10px] text-muted-foreground flex items-center gap-1"><Clock className="size-3" /> Awaiting accounting approval</span>}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </TableWrap>
    );
}


// ---------- Holiday pay tab ----------
// Saved directly by HR (audited), not through maker-checker: it is a company rule about who is paid for a holiday.
function HolidayPayTab({ canEdit }) {
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/payroll-settings/pay-policy")
            .then((r) => setForm({
                holiday_requires_prior_workday: !!r.data.holiday_requires_prior_workday,
                regular_holiday_premium: String(Math.round(r.data.regular_holiday_premium * 100)),
                special_holiday_premium: String(Math.round(r.data.special_holiday_premium * 100)),
            }))
            .catch((e) => toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed to load holiday pay settings"));
    }, []);

    if (!form) return null;
    const save = async () => {
        setSaving(true);
        try {
            await api.put("/payroll-settings/pay-policy", {
                holiday_requires_prior_workday: form.holiday_requires_prior_workday,
                regular_holiday_premium: Number(form.regular_holiday_premium) / 100,
                special_holiday_premium: Number(form.special_holiday_premium) / 100,
            });
            toast.success("Holiday pay settings saved");
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Could not save"); }
        setSaving(false);
    };

    return (
        <Surface className="p-4 space-y-4" data-testid="holiday-pay-tab">
            <div>
                <h3 className="font-display font-semibold">Holiday pay for this company</h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                    The extra paid on top of the day&apos;s ordinary pay when someone works a holiday, and whether an
                    unworked regular holiday is paid only to people who were present on their last scheduled workday
                    before it. The defaults (100% and 30%, rule off) are how payroll has always worked.
                </p>
            </div>
            <div className="grid grid-cols-2 gap-3 max-w-md">
                <div><Label>Worked regular holiday: extra %</Label>
                    <Input type="number" min="100" max="300" value={form.regular_holiday_premium} disabled={!canEdit} data-testid="hp-regular"
                           onChange={(e) => setForm({ ...form, regular_holiday_premium: e.target.value })} />
                    <div className="text-[10px] text-muted-foreground mt-0.5">100 = paid 200% of the day in all. Never below 100 (the legal minimum).</div></div>
                <div><Label>Worked special day: extra %</Label>
                    <Input type="number" min="30" max="300" value={form.special_holiday_premium} disabled={!canEdit} data-testid="hp-special"
                           onChange={(e) => setForm({ ...form, special_holiday_premium: e.target.value })} />
                    <div className="text-[10px] text-muted-foreground mt-0.5">30 = paid 130% of the day in all. Never below 30 (the legal minimum).</div></div>
            </div>
            <div className="flex items-start gap-2">
                <input type="checkbox" id="hp-prior" data-testid="hp-prior" checked={form.holiday_requires_prior_workday} disabled={!canEdit}
                       onChange={(e) => setForm({ ...form, holiday_requires_prior_workday: e.target.checked })} />
                <Label htmlFor="hp-prior" className="!mb-0 cursor-pointer">
                    An unworked regular holiday needs attendance on the last scheduled workday before it
                    <span className="block text-xs text-muted-foreground font-normal">
                        Present (or on paid leave) that day: the holiday is paid. Otherwise it is deducted for monthly
                        staff and unpaid for daily staff. A special non-working day stays no work, no pay.
                    </span>
                </Label>
            </div>
            {canEdit
                ? <button className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium" onClick={save} disabled={saving} data-testid="hp-save">Save</button>
                : <span className="badge badge-draft">View only</span>}
        </Surface>
    );
}

// ---------- Tardiness & Undertime tab ----------
function TardinessTab({ isHrManager, refreshKey, onProposed }) {
    const [policy, setPolicy] = useState(null);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ enabled: "false", grace_minutes: "0", rounding_minutes: "1", half_day_minutes: "0" });

    useEffect(() => {
        let alive = true;
        api.get("/payroll-settings/tardiness-policy")
            .then((r) => { if (alive) setPolicy(r.data); })
            .catch((e) => { if (alive) toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed to load tardiness policy"); });
        return () => { alive = false; };
    }, [refreshKey]);

    const openDialog = () => {
        setForm({
            enabled: policy?.enabled ? "true" : "false",
            grace_minutes: String(policy?.grace_minutes ?? 0),
            rounding_minutes: String(policy?.rounding_minutes ?? 1),
            half_day_minutes: String(policy?.half_day_minutes ?? 0),
        });
        setOpen(true);
    };

    const submit = async () => {
        const grace = Number(form.grace_minutes);
        const rounding = Number(form.rounding_minutes);
        if (!Number.isInteger(grace) || grace < 0 || grace > 480) return toast.error("Grace must be a whole number of minutes from 0 to 480");
        if (!Number.isInteger(rounding) || rounding < 1) return toast.error("Rounding must be a whole number of minutes, at least 1");
        const half = Number(form.half_day_minutes === "" ? 0 : form.half_day_minutes);
        if (!Number.isInteger(half) || half < 0 || half > 480) return toast.error("The half-day limit must be a whole number of minutes from 1 to 480, or 0 to turn it off");
        const body = { module: "tardiness_policy", action: "upsert", payload: { enabled: form.enabled === "true", grace_minutes: grace, rounding_minutes: rounding, half_day_minutes: half } };
        if (await propose(body, onProposed)) setOpen(false);
    };

    if (!policy) return null;

    return (
        <div className="space-y-5">
            <Surface className="p-4">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <h3 className="font-display font-semibold flex items-center gap-2"><Clock className="size-4 text-primary" /> Tardiness &amp; Undertime Deduction</h3>
                        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                            When on, time an employee did not work is deducted from the period&apos;s basic pay. Late arrival and undertime are de-duplicated, so the same missing minutes are never charged twice. Applies to monthly and semi-monthly payroll only, and never to a period already locked or paid.
                        </p>
                    </div>
                    <span className={policy.enabled ? "badge badge-approved" : "badge badge-draft"} data-testid="tardiness-state">{policy.enabled ? "ON" : "OFF"}</span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
                    <div>
                        <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Status</div>
                        <div className="text-sm mt-1">{policy.enabled ? "Deducting" : "Not deducting"}</div>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Grace per day</div>
                        <div className="text-sm mt-1" data-testid="tardiness-grace">{policy.grace_minutes} min</div>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Rounding</div>
                        <div className="text-sm mt-1" data-testid="tardiness-rounding">{policy.rounding_minutes === 1 ? "Per minute" : policy.rounding_minutes + " min blocks"}</div>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Half-day rule</div>
                        <div className="text-sm mt-1" data-testid="tardiness-half-day">{policy.half_day_minutes ? `Over ${policy.half_day_minutes} min late = half day` : "Off"}</div>
                    </div>
                </div>

                <p className="text-xs text-muted-foreground mt-4">
                    This policy applies to the company you are currently working in. It is separate from the per-shift grace that decides whether an arrival is <em>flagged</em> late, so tightening a report never starts costing anyone money by itself.
                </p>

                <div className="mt-4">
                    <ProposeGate isHrManager={isHrManager}>
                        <Dialog open={open} onOpenChange={setOpen}>
                            <DialogTrigger asChild>
                                <button className="h-9 px-3 rounded-md bg-primary text-white text-sm font-medium flex items-center gap-2" onClick={openDialog} data-testid="propose-tardiness"><Clock className="size-4" /> Propose Change</button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader><DialogTitle>Propose Tardiness &amp; Undertime Policy</DialogTitle></DialogHeader>
                                <div className="space-y-3">
                                    <div>
                                        <Label>Deduction</Label>
                                        <select className="h-9 w-full rounded-md border bg-background px-2 text-sm" value={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.value })} data-testid="tardiness-enabled">
                                            <option value="false">Off &mdash; report the time, deduct nothing</option>
                                            <option value="true">On &mdash; deduct unworked time from basic pay</option>
                                        </select>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <Label>Grace per day (min)</Label>
                                            <Input type="number" min="0" max="480" value={form.grace_minutes} onChange={(e) => setForm({ ...form, grace_minutes: e.target.value })} data-testid="tardiness-grace-input" />
                                        </div>
                                        <div>
                                            <Label>Rounding block (min)</Label>
                                            <Input type="number" min="1" value={form.rounding_minutes} onChange={(e) => setForm({ ...form, rounding_minutes: e.target.value })} data-testid="tardiness-rounding-input" />
                                        </div>
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        Grace forgives the combined shortfall for each day. A rounding block of 1 charges per minute; a larger block rounds the chargeable time up. Apart from the half-day rule below, chargeable time never exceeds the time actually missed.
                                    </p>
                                    <div>
                                        <Label>Half-day rule: late more than (min)</Label>
                                        <Input type="number" min="0" max="480" value={form.half_day_minutes} onChange={(e) => setForm({ ...form, half_day_minutes: e.target.value })} data-testid="tardiness-half-day-input" />
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        A day recorded <b>more than</b> this many minutes late (for example 120 for two hours) is charged as at least a half day, 4 hours of pay, even when the per-minute deduction above is Off. A day exactly at the limit is not a half day. A day later than 4 hours is charged what was actually missed. The limit looks at the recorded lateness before grace. Enter 0 to turn the rule off.
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        This is submitted for Accounting Manager approval and changes nobody&apos;s pay until approved. The approver cannot be you.
                                    </p>
                                </div>
                                <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={submit} data-testid="submit-tardiness">Submit for Approval</button></DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </ProposeGate>
                </div>
            </Surface>
        </div>
    );
}
